import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquareWarning, RefreshCw, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import ReportForm from "@/components/reports/ReportForm";
import ReportItem from "@/components/reports/ReportItem";
import { listReports } from "@/lib/api";
import { getStoredPin, setStoredPin, clearStoredPin, promptForPin } from "@/lib/adminPin";
import { changeAdminPin } from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
];

function FilterTabs({ filter, onChange, counts }) {
  return (
    <div className="flex items-center border border-slate-200 rounded-none overflow-hidden">
      {FILTERS.map((f) => {
        const count = f.key === "all" ? counts.total : counts[f.key] || 0;
        const active = filter === f.key;
        return (
          <button
            key={f.key}
            data-testid={`report-filter-${f.key}`}
            onClick={() => onChange(f.key)}
            className={
              "px-3 py-1.5 mono text-[11px] uppercase tracking-widest transition-colors flex items-center gap-1.5 " +
              (active
                ? "bg-[#2563EB] text-white font-semibold"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
            }
          >
            {f.label}
            <span className={active ? "text-black/70" : "text-slate-400"}>
              ({count})
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ReportsListSection({ loading, filtered, filter, refresh }) {
  if (loading) {
    return <div className="p-6 mono text-sm text-[#64748B]">Loading…</div>;
  }
  if (filtered.length === 0) {
    const filterLabel = FILTERS.find((f) => f.key === filter)?.label.toLowerCase();
    const emptyMsg = filter === "all"
      ? "No reports yet. Be the first to submit one."
      : `No ${filterLabel} reports.`;
    return (
      <div className="p-8 border border-dashed border-slate-200 text-center text-[#64748B] text-sm">
        {emptyMsg}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="reports-list">
      {filtered.map((r) => (
        <ReportItem key={r.id} report={r} onChanged={refresh} />
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [adminUnlocked, setAdminUnlocked] = useState(!!getStoredPin());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listReports()
      .then((data) => {
        if (alive) setItems(data);
      })
      .catch(() => toast.error("Failed to load reports"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const toggleAdmin = () => {
    if (adminUnlocked) {
      clearStoredPin();
      setAdminUnlocked(false);
      toast.info("Admin locked. Status/notes/delete will require the PIN again.");
    } else {
      const pin = promptForPin();
      if (pin) {
        setAdminUnlocked(true);
        toast.success("Admin PIN saved on this browser");
      }
    }
  };

  const openChangePin = async () => {
    const currentPin = getStoredPin();
    if (!currentPin) {
      toast.error("Unlock admin first with your current PIN");
      return;
    }
    // eslint-disable-next-line no-alert
    const newPin = window.prompt("Enter a NEW admin PIN (4–32 chars, no spaces):");
    if (newPin === null) return;
    const trimmed = (newPin || "").trim();
    if (trimmed.length < 4) {
      toast.error("PIN must be at least 4 characters");
      return;
    }
    // eslint-disable-next-line no-alert
    const confirm = window.prompt("Re-enter the new PIN to confirm:");
    if (confirm === null) return;
    if (confirm.trim() !== trimmed) {
      toast.error("PINs did not match");
      return;
    }
    try {
      await changeAdminPin(currentPin, trimmed);
      setStoredPin(trimmed);
      toast.success("Admin PIN changed and saved");
    } catch (err) {
      if (err?.response?.status === 401) {
        clearStoredPin();
        setAdminUnlocked(false);
        toast.error("Current PIN was rejected. You've been locked out — unlock again.");
      } else {
        toast.error(err?.response?.data?.detail || "PIN change failed");
      }
    }
  };

  const counts = useMemo(() => {
    const c = { total: items.length, open: 0, in_progress: 0, resolved: 0 };
    for (const r of items) {
      if (r.status in c) c[r.status]++;
    }
    return c;
  }, [items]);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((r) => r.status === filter)),
    [items, filter],
  );

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="min-h-screen relative">
      <header className="border-b border-slate-200 sticky top-0 z-30 bg-[#FFFFFF]/90 backdrop-blur-md">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="group">
              <Logo size={40} className="transition-transform duration-300 group-hover:scale-105" />
            </Link>
            <div>
              <div className="heading text-xl font-bold tracking-tight">
                CARGOLOAD<span className="text-[#2563EB]">.PRO</span>
              </div>
              <div className="overline">Report an Issue</div>
            </div>
          </div>
          <Link
            to="/"
            data-testid="reports-back-btn"
            className="mono text-[11px] uppercase tracking-widest text-slate-600 hover:text-[#2563EB] flex items-center gap-2 border border-slate-200 px-3 py-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Load Planner
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-10 py-8 max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: submit form */}
        <div className="lg:col-span-2">
          <div className="overline mb-3">Submit</div>
          <h1 className="heading text-3xl md:text-4xl font-bold mb-4 leading-[1.05]">
            Spot something <span className="text-[#2563EB]">off</span>?
          </h1>
          <p className="text-[#64748B] text-sm leading-relaxed mb-6 max-w-md">
            Attach the packing list or a screenshot. We look at every report and
            update the status here.
          </p>
          <ReportForm onSubmitted={refresh} />
        </div>

        {/* Right: reports list */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">All submitted reports</div>
              <h2 className="heading text-2xl font-semibold mt-1 flex items-center gap-2">
                <MessageSquareWarning className="w-5 h-5 text-[#2563EB]" /> Reports
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleAdmin}
                data-testid="admin-toggle-btn"
                className={
                  "mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors flex items-center gap-1.5 " +
                  (adminUnlocked
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                    : "border-slate-200 text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB]")
                }
                title={adminUnlocked
                  ? "Admin unlocked on this browser. Click to lock."
                  : "Unlock admin (status change / delete / notes) with the PIN"}
              >
                {adminUnlocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {adminUnlocked ? "Admin unlocked" : "Admin locked"}
              </button>
              {adminUnlocked && (
                <button
                  onClick={openChangePin}
                  data-testid="change-pin-btn"
                  className="mono text-[10px] uppercase tracking-widest px-3 py-2 border border-slate-200 text-slate-500 hover:text-[#2563EB] hover:border-[#2563EB] transition-colors"
                  title="Change the admin PIN"
                >
                  Change PIN
                </button>
              )}
              <button
                onClick={refresh}
                data-testid="refresh-reports-btn"
                className="text-slate-500 hover:text-[#2563EB] transition-colors p-2"
                aria-label="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mb-4">
            <FilterTabs filter={filter} onChange={setFilter} counts={counts} />
          </div>

          <ReportsListSection
            loading={loading}
            filtered={filtered}
            filter={filter}
            refresh={refresh}
          />
        </div>
      </main>

      <footer className="mt-16 border-t border-slate-200 py-6 px-6 md:px-10">
        <div className="max-w-[1200px] mx-auto mono text-xs text-[#64748B]">
          CARGOLOAD.PRO — Public issue tracker. Attachments capped at 10 MB.
        </div>
      </footer>
    </div>
  );
}
